/**
 * Utility functions to perform custom functions
 */
import showdown from "showdown";
import { groupConfig } from "../config/groupConfig";

class Utils {
  /**
   * Get the HTML equivalent to Markdown text
   * @param {*} markdownText - Markdown text
   * @returns HTML equivalent to Markdown text
   */
  markdownToHtml = (markdownText) => {
    const converter = new showdown.Converter();
    const vMarkdownText = (typeof(markdownText) !== "string") ? markdownText.toString() : markdownText;
    return converter.makeHtml(vMarkdownText);
  };

  /**
   * Function to split excel cell as row and column
   * @param {*} cell
   * @returns
   */
  splitExcelCell = (cell) => {
    let match = cell.match(/^([A-Z]+)(\d+)$/);
    if (match) {
      return { column: match[1], row: parseInt(match[2]) };
    }
  };

  /**
   * Function to convert numbers to letters incase of excel rows & coloumns references
   * @param {*} num
   * @returns
   */
  numToLetters = (num) => {
    let letters = "";
    while (num > 0) {
      let remainder = (num - 1) % 26;
      letters = String.fromCharCode(65 + remainder) + letters;
      num = Math.floor((num - remainder) / 26);
    }
    return letters;
  };

  /**
   * Function to convert letters to numbers incase of excel rows & coloumns references
   * @param {*} letter
   * @returns
   */
  letterToNum = (letter) => {
    letter = letter.toUpperCase();
    let num = 0;
    let len = letter.length;
    for (let pos = 0; pos < len; pos++) {
      num *= 26;
      num += letter.charCodeAt(pos) - 64;
    }
    return num;
  };

  /**
   * Check if the group name provided is in the SAT group name configuration
   * @param {groupDisplayName} groupDisplayName Group name to check as SAT Group name
   * @returns true if provided group name is SAT group name, otherwise false
   */
  isSatGroup(groupDisplayName) {
    //REACT_APP_GROUPNAME_DAISEY configuration contains comma (,) separated group names
    const satGroupNames = groupConfig.groupNameDaisEy.toLowerCase().split(",");
    return (
      satGroupNames.findIndex(
        (sgn) => sgn.trim() === groupDisplayName.toLowerCase().trim()
      ) !== -1
    );
  }

  /**
   * Check if the group name provided is in the Platform Admin group name configuration
   * @param {groupDisplayName} groupDisplayName Group name to check as Platform Admin Group name
   * @returns true if provided group name is Platform Admin group name, otherwise false
   */
  isPlatformAdminGroup(groupDisplayName) {
    //REACT_APP_GROUPNAME_PLATFORM_ADMIN configuration contains comma (,) separated group names
    const platformAdminGroupNames = groupConfig.groupNamePlatformAdmin
      .toLowerCase()
      .split(",");
    return (
      platformAdminGroupNames.findIndex(
        (pagn) => pagn.trim() === groupDisplayName.toLowerCase().trim()
      ) !== -1
    );
  }

  /**
   * Function to write cell value in excel
   * @param {*} cellType
   * @param {*} cell
   * @param {*} value
   */
  writeValueToExcelCell = (cellType, cell, value) => {
    cell.format.font.name = "Arial";
    cell.format.font.size = 8;
    if (cellType === "Header") {
      if (value === "") {
        cell.format.fill.color = "white";
      } else {
        cell.format.fill.color = "Blue";
      }

      cell.format.font.color = "White";
      cell.numberFormat = [["General"]];
    } else {
      cell.format.fill.color = "white";
      cell.format.font.color = "black";
      cell.numberFormat = "@";
      cell.format.columnWidth = 100;
      cell.format.rowHeight = 12.75;
    }
    cell.format.horizontalAlignment = "Left";
    cell.format.verticalAlignment = "Top";
    cell.format.wrapText = true;
    cell.values = [[value]];
  };

  /**
   * Function to get cell value
   * @param {*} vRow
   * @param {*} vCol
   * @param {*} vSheet
   * @param {*} vContext
   * @returns
   */
  getCellValue = async (vRow, vCol, vSheet, vContext) => {
    const cellRange = `${vCol}${vRow}`;
    const curCell = vSheet.getRange(cellRange);
    curCell.load("format/font");
    curCell.load("values");
    await vContext.sync();
    return curCell.values[0][0];
  };

  /**
   * Function to get cell to write line commentary
   * @param {*} vSheet
   * @param {*} vRange
   * @param {*} vContext
   * @returns
   */
  getColumnLineCommentary = async (vSheet, vRange, vContext) => {
    const vLineCommentaryHeaderText = "Line Commentary";
    const firstCell = this.splitExcelCell(vRange.split(":")[0]);
    const lastCell = this.splitExcelCell(vRange.split(":")[1]);
    const vRow = firstCell.row;
    let vColumn = this.letterToNum(lastCell.column);
    const noOfCols = 100;
    for (let i = vColumn + 1; i < noOfCols; i++) {
      const curColumn = this.numToLetters(i);
      const curCellValue = await this.getCellValue(
        vRow,
        curColumn,
        vSheet,
        vContext
      );
      if (curCellValue === vLineCommentaryHeaderText) {
        return { column: curColumn, row: vRow };
      }

      const vStartCell = `${curColumn}${vRow}`;
      const vEndCell = `${curColumn}${lastCell.row + 3}`;
      const vIsEmpty = await this.isExcelRangeEmpty(
        vStartCell,
        vEndCell,
        vSheet,
        vContext
      );

      if (vIsEmpty) {
        const nextColumn = this.numToLetters(i + 1);
        const nextCellValue = await this.getCellValue(
          vRow,
          nextColumn,
          vSheet,
          vContext
        );

        const vNextColumnStartCell = `${nextColumn}${vRow}`;
        const vNextColumnEndCell = `${nextColumn}${lastCell.row + 3}`;
        const vNextColumnIsEmpty = await this.isExcelRangeEmpty(
          vNextColumnStartCell,
          vNextColumnEndCell,
          vSheet,
          vContext
        );

        if (vNextColumnIsEmpty || nextCellValue === vLineCommentaryHeaderText) {
          return { column: nextColumn, row: vRow };
        }
      }
    }
    return null;
  };

  /**
   * Function to check the range is empty
   * @param {*} startCell
   * @param {*} endCell
   * @param {*} vSheet
   * @param {*} vContext
   * @returns isEmpty
   */
  isExcelRangeEmpty = async (startCell, endCell, vSheet, vContext) => {
    const cellRange = `${startCell}:${endCell}`;
    const curRange = vSheet.getRange(cellRange);
    curRange.load("values");
    await vContext.sync();
    const isEmpty = curRange.values.every((row) =>
      row.every((cell) => cell === "")
    );
    return isEmpty;
  };

  /**
   * Function to format date string
   * @param {*} vDate
   * @param {*} vFormat
   * @returns
   */
  formatDateString = (vDate, vFormat) => {
    const dtDate = new Date(vDate);
    let strDay;
    let strMonth;
    let strYear;
    let strDate = "";

    if (vFormat === "Mmm DD, YYYY") {
      strDay = dtDate.toLocaleString("default", { day: "2-digit" });
      strMonth = dtDate.toLocaleString("default", { month: "short" });
      strYear = dtDate.toLocaleString("default", { year: "numeric" });

      strDate = `${strMonth} ${strDay}, ${strYear}`;
    }

    return strDate;
  };
}
export default Utils;
